/**
 * PlayerCardPicker — single-player analytics view (PMA-COCK-168).
 *
 * Replaces the old all-cards grid (`player-cards-grid`, retired) with: a
 * home/away segmented control, a horizontal chip row (shirt number + short
 * name, starters first then bench, each sorted by jersey number), and one
 * `PlayerCard` for the current selection. Default selection is the first
 * home starter. Selection lives in component state only (not persisted).
 *
 * A roster player who has not yet generated any event has no entry in
 * `usePlayerStats`'s output (it only aggregates players who appear in
 * `events`) — `buildEmptyPlayerStats` synthesizes an all-zero `PlayerStats`
 * from the roster entry itself so every chip is always selectable, not just
 * the players who have already logged something.
 */
import React, { useMemo, useState } from "react";
import PlayerCard from "./PlayerCard";
import { formatDisplayName } from "../../../../lib/nameFormat";
import type { MatchEvent } from "../../../../store/useMatchLogStore";
import type { Match, Player } from "../../types";
import type { PlayerStats } from "../../hooks/usePlayerStats";
import { computeMinutesPlayed } from "../../utils/minutesPlayed";

type TeamSideKey = "home" | "away";

/**
 * Every non-identity field of `PlayerStats`, zeroed. Kept as an explicit
 * `Omit<...>`-typed template (not spread from a live entry) so that any
 * FUTURE field added to `PlayerStats` fails `tsc` here until this template
 * is updated too, instead of silently rendering `undefined` for a roster
 * player with no events yet.
 */
const ZERO_STATS_TEMPLATE: Omit<
  PlayerStats,
  | "playerId"
  | "playerName"
  | "jerseyNumber"
  | "teamSide"
  | "photoUrl"
  | "photoImage"
> = {
  totalEvents: 0,
  passesGood: 0,
  passesBad: 0,
  passesReceived: 0,
  shots: 0,
  shotsOnTarget: 0,
  shotsOffTarget: 0,
  shotsBlocked: 0,
  goals: 0,
  penalties: 0,
  penaltyGoals: 0,
  penaltiesWon: 0,
  shootoutKicks: 0,
  shootoutGoals: 0,
  assists: 0,
  duelsWon: 0,
  duelsLost: 0,
  foulsCommitted: 0,
  foulsReceived: 0,
  interceptions: 0,
  recoveries: 0,
  clearances: 0,
  blocks: 0,
  saves: 0,
  yellowCards: 0,
  redCards: 0,
  touches: 0,
};

function buildEmptyPlayerStats(
  player: Player,
  teamSide: TeamSideKey,
): PlayerStats {
  return {
    ...ZERO_STATS_TEMPLATE,
    playerId: player.id,
    playerName: player.short_name || player.full_name,
    jerseyNumber: player.jersey_number,
    teamSide,
    photoUrl: player.photo_url ?? null,
    photoImage: player.photo_image ?? null,
  };
}

/** Starters first, then bench — each group sorted by jersey number. */
function orderRoster(players: Player[]): Player[] {
  const starters = players
    .filter((p) => p.is_starter)
    .sort((a, b) => a.jersey_number - b.jersey_number);
  const bench = players
    .filter((p) => !p.is_starter)
    .sort((a, b) => a.jersey_number - b.jersey_number);
  return [...starters, ...bench];
}

interface PlayerCardPickerProps {
  match: Match;
  /** Output of `usePlayerStats(match, events)` — only carries players who
   * are the author/receiver of at least one event. */
  playerStats: PlayerStats[];
  events: MatchEvent[];
  hidePlayerHeatmaps?: boolean;
  /** Current match clock in seconds (open stints end here). EPIC-107 D1. */
  currentClockSeconds?: number;
  /** Period (1-4) `currentClockSeconds` belongs to (broadcast frame). */
  currentPeriod?: number;
  /** `currentClockSeconds` is already one continuous total (COMPLETED/PENALTIES). */
  matchFinished?: boolean;
  t: (key: string, fallback?: string) => string;
}

const PlayerCardPicker: React.FC<PlayerCardPickerProps> = ({
  match,
  playerStats,
  events,
  hidePlayerHeatmaps = false,
  currentClockSeconds = 0,
  currentPeriod,
  matchFinished = false,
  t,
}) => {
  const homeRoster = useMemo(
    () => orderRoster(match.home_team.players ?? []),
    [match.home_team.players],
  );
  const awayRoster = useMemo(
    () => orderRoster(match.away_team.players ?? []),
    [match.away_team.players],
  );

  const [selectedTeam, setSelectedTeam] = useState<TeamSideKey>("home");
  const [selectedPlayerId, setSelectedPlayerId] = useState<string>(
    () => homeRoster[0]?.id ?? "",
  );

  const roster = selectedTeam === "home" ? homeRoster : awayRoster;
  const team = selectedTeam === "home" ? match.home_team : match.away_team;
  const minutesByPlayer = useMemo(
    () =>
      computeMinutesPlayed(
        events,
        {
          home: match.home_team.players ?? [],
          away: match.away_team.players ?? [],
        },
        currentClockSeconds,
        currentPeriod,
        {
          matchFinished,
          matchTotalSeconds:
            (match.match_time_seconds ?? 0) +
            (match.ineffective_time_seconds ?? 0),
          periodTimestamps: match.period_timestamps,
        },
      ),
    [
      events,
      match.home_team.players,
      match.away_team.players,
      currentClockSeconds,
      currentPeriod,
      matchFinished,
      match.period_timestamps,
      match.match_time_seconds,
      match.ineffective_time_seconds,
    ],
  );

  const handleTeamChange = (nextTeam: TeamSideKey) => {
    setSelectedTeam(nextTeam);
    const nextRoster = nextTeam === "home" ? homeRoster : awayRoster;
    setSelectedPlayerId(nextRoster[0]?.id ?? "");
  };

  const statsByPlayerId = useMemo(() => {
    const map = new Map<string, PlayerStats>();
    for (const s of playerStats) map.set(s.playerId, s);
    return map;
  }, [playerStats]);

  const selectedPlayer = roster.find((p) => p.id === selectedPlayerId);
  const selectedStats = selectedPlayer
    ? (statsByPlayerId.get(selectedPlayer.id) ??
      buildEmptyPlayerStats(selectedPlayer, selectedTeam))
    : undefined;

  return (
    <div className="flex flex-col gap-3" data-testid="player-card-picker">
      <div className="flex rounded-lg border border-slate-700 overflow-hidden self-start">
        {(["home", "away"] as const).map((side) => {
          const label =
            side === "home"
              ? match.home_team.short_name || match.home_team.name
              : match.away_team.short_name || match.away_team.name;
          const isActive = selectedTeam === side;
          return (
            <button
              key={side}
              type="button"
              data-testid={`player-card-picker-team-${side}`}
              aria-pressed={isActive}
              onClick={() => handleTeamChange(side)}
              className={`min-h-11 min-w-11 px-3 py-1.5 text-xs font-semibold transition-colors ${
                isActive
                  ? "bg-slate-700 text-slate-100"
                  : "text-slate-400 hover:text-slate-200 hover:bg-slate-800"
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>

      {roster.length === 0 ? (
        <div
          className="rounded-lg border border-dashed border-slate-700 bg-slate-900/40 px-4 py-6 text-center text-xs text-slate-400"
          data-testid="player-card-picker-empty"
        >
          {t("analytics.playerCardPickerEmpty", "No roster players yet")}
        </div>
      ) : (
        <>
          <div
            className="flex gap-2 overflow-x-auto pb-1"
            data-testid="player-card-picker-chips"
          >
            {roster.map((player) => {
              const isSelected = player.id === selectedPlayerId;
              return (
                <button
                  key={player.id}
                  type="button"
                  data-testid={`player-card-chip-${player.id}`}
                  aria-pressed={isSelected}
                  onClick={() => setSelectedPlayerId(player.id)}
                  className={`shrink-0 inline-flex min-h-11 items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold whitespace-nowrap transition-colors ${
                    isSelected
                      ? "border-indigo-400 bg-indigo-500/20 text-indigo-100"
                      : "border-slate-700 bg-slate-800/70 text-slate-300 hover:bg-slate-700"
                  }`}
                >
                  <span className="tabular-nums text-slate-400">
                    #{player.jersey_number}
                  </span>
                  <span>
                    {formatDisplayName(player.short_name || player.full_name)}
                  </span>
                </button>
              );
            })}
          </div>

          {selectedStats && (
            <PlayerCard
              stats={selectedStats}
              events={events}
              team={team}
              hidePlayerHeatmaps={hidePlayerHeatmaps}
              minutesPlayed={minutesByPlayer[selectedStats.playerId] ?? 0}
              t={t}
            />
          )}
        </>
      )}
    </div>
  );
};

export default PlayerCardPicker;
